/* This example requires Tailwind CSS v2.0+ */
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CreateDropdownProps } from './CreateDropdown.types';
import { checkPermission } from '../../utils/auth';
import { P } from '../../utils/permissions';
import { useMarketingTierGate } from '../../hooks/useMarketingTierEnabled';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { PlusIcon } from '@heroicons/react/24/outline';
import CreateFundraiserDrawer from '../drawers/CreateFundraiserDrawer';
import CreateFundraisingPaymentDrawer from '../drawers/CreateFundraisingPaymentDrawer';
import CreateEventPaymentDrawer from '../drawers/CreateEventPaymentDrawer';
// @ts-expect-error - JS file without type definitions
import CreateDonationFlagDrawer from '../drawers/CreateDonationFlagDrawer';
// @ts-expect-error - JS file without type definitions
import CreateEventDrawer from '../drawers/CreateEventDrawer';
// @ts-expect-error - JS file without type definitions
import CreateTicketDrawer from '../drawers/CreateTicketDrawer';
import CreateEditContactDrawer from '../drawers/CreateEditContactDrawer';
// @ts-expect-error - JS file without type definitions
import FormSelectionModal from '../modals/FormSelectionModal';
// @ts-expect-error - JS file without type definitions
import ElementSelectionModal from '../modals/ElementSelectionModal';
// @ts-expect-error - JS file without type definitions
import PaymentSelectionModal from '../modals/PaymentSelectionModal';
import CreateAddToCartDrawer from '../drawers/CreateAddToCartDrawer';
import CreateFundraisingBarDrawer from '../drawers/CreateFundraisingBarDrawer';
import CreateQRCodeDrawer from '../drawers/CreateQRCodeDrawer';
import CreateDonorListDrawer from '../drawers/CreateDonorListDrawer';
import CreateFundraisingFlagDrawer from '../drawers/CreateFundraisingFlagDrawer';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import URLBuilderDrawer from '../drawers/URLBuilderDrawer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { ELEMENT_FEATURE_KEYS, FEATURE } from '../../utils/entitlements';

const PAYMENT_GATED_TOOLTIP =
  'Stripe is reviewing your account. Payments will be available shortly.';
const CREATE_MENU_TOOLTIP = 'Create a new item';

export default function CreateDropdown(props: CreateDropdownProps) {
  const history = useHistory();
  const marketingEnabled = useMarketingTierGate();
  // Entitlement ROUTING (PE-1624 → PE-1759, Ali 2026-08-12): the Element /
  // Broadcast / Segment items always render (permission + flag gates only) —
  // but for a NON-entitled org (Starter, incl. downgraded) they navigate to
  // the surface PAGE (/elements, /broadcasts, /segments — advertising or
  // read-only view per the record matrix) instead of the creation UI, which
  // the server would reject anyway. Fail-open when resolution is degraded
  // (corrupt billingDetails) → creation UI, where the server stays authority.
  const { can: canEntitle, degraded: entDegraded } = useEntitlements();
  const canCreateElement = entDegraded || ELEMENT_FEATURE_KEYS.some((f) => canEntitle(f));
  const canCreateBroadcast = entDegraded || canEntitle(FEATURE.BROADCASTS);
  const canCreateSegment = entDegraded || canEntitle(FEATURE.SEGMENTS);
  const [openForm, setOpenForm] = useState(false);
  const [openEvent, setOpenEvent] = useState(false);
  const [openTicket, setOpenTicket] = useState(false);

  const [openFundraisingPayment, setOpenFundraisingPayment] = useState(false);
  const [openEventPayment, setOpenEventPayment] = useState(false);
  const [openDonationFlag, setOpenDonationFlag] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [openFormSelection, setOpenFormSelection] = useState(false);
  const [openElementSelection, setOpenElementSelection] = useState(false);
  const [openPaymentSelection, setOpenPaymentSelection] = useState(false);

  // New individual element drawer states
  const [openAddToCart, setOpenAddToCart] = useState(false);
  const [openFundraisingBar, setOpenFundraisingBar] = useState(false);
  const [openQRCode, setOpenQRCode] = useState(false);
  const [openDonorList, setOpenDonorList] = useState(false);
  const [openFundraisingFlag, setOpenFundraisingFlag] = useState(false);
  const [openURLBuilder, setOpenURLBuilder] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onboardingDriver = useOnboardingDriver();
  const gatedUntilStripe = onboardingDriver?.chargesEnabled !== true;

  useEffect(() => {
    onboardingDriver?.setFormSelectionModalOpen(openFormSelection);
  }, [openFormSelection, onboardingDriver]);
  useEffect(() => {
    onboardingDriver?.setCreateDrawerOpen(openForm);
  }, [openForm, onboardingDriver]);

  const { readOnly } = useSelector((state: any) => state.readOnly);

  // Handle element type selection and open appropriate drawer
  const handleElementTypeSelect = (elementType: string): void => {
    history.push(`/elements/new/${elementType}`);
  };
  const handlePaymentTypeSelect = (paymentType: string): void => {
    if (gatedUntilStripe) return;
    history.push(`/payments/new/${paymentType}`);
  };

  const hasAnyCreatePermission =
    checkPermission(P.PROCESS_CONTACTS) ||
    checkPermission(P.PROCESS_FORMS) ||
    checkPermission(P.PROCESS_PAYMENTS) ||
    checkPermission(P.PROCESS_ELEMENTS) ||
    (marketingEnabled &&
      (checkPermission(P.CREATE_BROADCASTS) ||
        checkPermission(P.CREATE_SEGMENTS)));

  if (readOnly || !hasAnyCreatePermission) return null;

  const triggerIconClassName = '!h-4 !w-4 text-current';
  const triggerSurfaceClassName = props.toolbar
    ? 'rounded-md text-ink-muted transition duration-150 hover:bg-paper-inset hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0'
    : 'rounded-md border border-edge bg-white text-ink-muted shadow-sm shadow-black/[0.02] transition duration-150 hover:bg-paper-inset hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0';

  const createTrigger = props.showLabel ? (
    <Button
      variant={props.toolbar ? 'ghost' : 'outline'}
      className={`flex min-w-0 items-center justify-center gap-1 ${triggerSurfaceClassName} ${
        props.toolbar
          ? props.collapseLabelBelowLg
            ? 'h-7 w-7 px-0 py-0 lg:w-[76px] lg:px-2'
            : 'h-7 w-[76px] px-2 py-0'
          : props.compactOnDesktop
            ? 'h-8 w-full px-3 lg:w-8 lg:px-1'
            : 'h-8 w-full px-3'
      }`}
      aria-label="Create"
    >
      <PlusIcon className={triggerIconClassName} strokeWidth={2} />
      <span
        className={`text-sm font-medium ${props.toolbar ? 'text-current' : 'text-ink-secondary'} ${
          props.collapseLabelBelowLg
            ? 'sr-only lg:not-sr-only'
            : props.compactOnDesktop
              ? 'lg:hidden'
              : ''
        }`}
      >
        Create
      </span>
    </Button>
  ) : (
    <Button
      variant={props.toolbar ? 'ghost' : 'outline'}
      size="icon"
      aria-label="Create"
      className={`${props.toolbar ? 'h-7 w-7 p-0' : 'h-8 w-8 p-1'} ${triggerSurfaceClassName}`}
    >
      <PlusIcon className={triggerIconClassName} strokeWidth={2} />
    </Button>
  );

  const dropdownTrigger = <DropdownMenuTrigger asChild>{createTrigger}</DropdownMenuTrigger>;
  const shouldShowTriggerTooltip = props.toolbar || !props.showLabel || props.compactOnDesktop;
  const triggerTooltipSide = props.showLabel && props.compactOnDesktop ? 'right' : 'bottom';
  const triggerTooltip = shouldShowTriggerTooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>{dropdownTrigger}</TooltipTrigger>
      <TooltipContent
        side={triggerTooltipSide}
        aria-label={CREATE_MENU_TOOLTIP}
        data-label={CREATE_MENU_TOOLTIP}
        className="max-w-[18rem] text-center leading-snug before:content-[attr(data-label)]"
      />
    </Tooltip>
  ) : (
    dropdownTrigger
  );

  return (
    <div className={props.showLabel ? 'flex w-full justify-center' : undefined}>
      {openForm && (
        <CreateFundraiserDrawer
          grabSelectableFunds={props.grabSelectableFunds}
          open={openForm}
          setOpen={setOpenForm}
          selectableFunds={props.selectableFunds}
          merchant={props.merchant}
          onFormCreated={(form: any) => {
            // Call the parent's callback to update the donations list
            if (props.onFormCreated) {
              props.onFormCreated(form);
            }
          }}
        />
      )}
      {openFundraisingPayment && (
        <CreateFundraisingPaymentDrawer
          grabSelectableFunds={
            props.grabSelectableFunds
              ? (token) => props.grabSelectableFunds?.(token || '')
              : undefined
          }
          open={openFundraisingPayment}
          setOpen={setOpenFundraisingPayment}
          selectableFunds={props.selectableFunds}
          merchant={props.merchant ?? null}
          selectableForms={props.selectableForms}
          onTransactionCreated={(transaction: any) => {
            // Call the parent's callback to update the donations list
            if (props.onTransactionCreated) {
              props.onTransactionCreated(transaction);
            }
          }}
        />
      )}
      {openEventPayment && (
        <CreateEventPaymentDrawer
          grabSelectableFunds={
            props.grabSelectableFunds
              ? (token) => props.grabSelectableFunds?.(token || '')
              : undefined
          }
          open={openEventPayment}
          setOpen={setOpenEventPayment}
          selectableFunds={props.selectableFunds}
          merchant={props.merchant ?? null}
          selectableForms={props.selectableForms}
          onTransactionCreated={(transaction: any) => {
            // Call the parent's callback to update the donations list
            if (props.onTransactionCreated) {
              props.onTransactionCreated(transaction);
            }
          }}
        />
      )}
      {openDonationFlag && (
        <CreateDonationFlagDrawer
          grabSelectableFunds={props.grabSelectableFunds}
          open={openDonationFlag}
          setOpen={setOpenDonationFlag}
          selectableFunds={props.selectableFunds}
          merchant={props.merchant}
          selectableForms={props.selectableForms}
        />
      )}
      {/* New individual element drawers - these fetch forms internally */}
      {openAddToCart && (
        <CreateAddToCartDrawer
          open={openAddToCart}
          setOpen={setOpenAddToCart}
          merchant={props.merchant}
        />
      )}
      {openFundraisingBar && (
        <CreateFundraisingBarDrawer
          open={openFundraisingBar}
          setOpen={setOpenFundraisingBar}
          merchant={props.merchant}
        />
      )}
      {openQRCode && (
        <CreateQRCodeDrawer open={openQRCode} setOpen={setOpenQRCode} merchant={props.merchant} />
      )}
      {openDonorList && (
        <CreateDonorListDrawer
          open={openDonorList}
          setOpen={setOpenDonorList}
          merchant={props.merchant}
        />
      )}
      {openFundraisingFlag && (
        <CreateFundraisingFlagDrawer
          open={openFundraisingFlag}
          setOpen={setOpenFundraisingFlag}
          merchant={props.merchant}
        />
      )}
      {openURLBuilder && (
        <URLBuilderDrawer
          open={openURLBuilder}
          setOpen={setOpenURLBuilder}
          merchant={props.merchant}
        />
      )}
      {openEvent && (
        <CreateEventDrawer
          grabSelectableFunds={props.grabSelectableFunds}
          open={openEvent}
          setOpen={setOpenEvent}
          selectableFunds={props.selectableFunds}
          merchant={props.merchant}
          selectableForms={props.selectableForms}
          // onFormCreated={props.onFormCreated}
          onFormCreated={(form: any) => {
            // Call the parent's callback to update the donations list
            if (props.onFormCreated) {
              props.onFormCreated(form);
            }
          }}
        />
      )}
      {openTicket && (
        <CreateTicketDrawer
          grabSelectableFunds={props.grabSelectableFunds}
          open={openTicket}
          setOpen={setOpenTicket}
          selectableFunds={props.selectableFunds}
          merchant={props.merchant}
          selectableForms={props.selectableForms}
        />
      )}
      {openContact && (
        <CreateEditContactDrawer
          open={openContact}
          setOpen={setOpenContact}
          merchant={props.merchant}
          onContactSaved={(contact) => {
            // Call the parent's callback to update the contacts list
            if (props.onContactCreated) {
              props.onContactCreated(contact);
            }
          }}
        />
      )}
      {openFormSelection && (
        <FormSelectionModal
          open={openFormSelection}
          setOpen={setOpenFormSelection}
          onFormTypeSelect={(formType: string) => {
            if (formType === 'fundraiser') {
              setOpenForm(true);
            } else if (formType === 'event') {
              setOpenEvent(true);
            }
          }}
        />
      )}
      {openElementSelection && (
        <ElementSelectionModal
          open={openElementSelection}
          setOpen={setOpenElementSelection}
          onElementTypeSelect={handleElementTypeSelect}
          merchantName={props.merchant?.merchantName}
          shareBlocked={false}
        />
      )}
      {openPaymentSelection && (
        <PaymentSelectionModal
          open={openPaymentSelection}
          setOpen={setOpenPaymentSelection}
          onPaymentTypeSelect={handlePaymentTypeSelect}
        />
      )}

      <DropdownMenu
        open={dropdownOpen}
        onOpenChange={(open) => {
          setDropdownOpen(open);
          onboardingDriver?.setCreateDropdownOpen(open);
        }}
      >
        {triggerTooltip}
        <DropdownMenuContent
          className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-[min(640px,calc(100vw-2rem))] overflow-y-auto rounded-xl border border-edge bg-white p-1.5 lg:w-[204px] lg:rounded-lg"
          side="bottom"
          align="start"
          sideOffset={5}
        >
          <div className="space-y-0.5">
            {checkPermission(P.PROCESS_CONTACTS) && (
              <DropdownMenuItem
                className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-sm transition-all duration-200 ease-in-out hover:bg-paper-inset"
                onClick={() => history.push('/contacts/new')}
              >
                <div>
                  <p className="font-semibold text-ink-secondary">Contact</p>
                  <p className="mt-0.5 text-[11px] font-normal text-ink-muted">Add a new contact</p>
                </div>
                <svg
                  className="ml-2 h-4 w-4 text-ink-muted transition-transform duration-200 ease-in-out group-hover:translate-x-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </DropdownMenuItem>
            )}

            {checkPermission(P.PROCESS_FORMS) && (
              <DropdownMenuItem
                className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-sm transition-all duration-200 ease-in-out hover:bg-paper-inset"
                onClick={() => {
                          history.push('/forms/new');
                }}
              >
                <div>
                  <p className="font-semibold text-ink-secondary">Form</p>
                  <p className="mt-0.5 text-[11px] font-normal text-ink-muted">Create a new form</p>
                </div>
                <svg
                  className="ml-2 h-4 w-4 text-ink-muted transition-transform duration-200 ease-in-out group-hover:translate-x-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </DropdownMenuItem>
            )}

            {checkPermission(P.PROCESS_PAYMENTS) &&
              (gatedUntilStripe ? (
                <TooltipProvider key="payment-gated">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <DropdownMenuItem
                          disabled
                          className="group flex cursor-not-allowed items-center justify-between rounded-md px-3 py-1.5 text-sm opacity-50"
                          onSelect={(e) => e.preventDefault()}
                        >
                          <div>
                            <p className="font-semibold text-ink-secondary">Payment</p>
                            <p className="mt-0.5 text-[11px] font-normal text-ink-muted">
                              Process a new transaction
                            </p>
                          </div>
                          <svg
                            className="ml-2 h-4 w-4 text-ink-muted"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </DropdownMenuItem>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{PAYMENT_GATED_TOOLTIP}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <DropdownMenuItem
                  key="payment-open"
                  className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-sm transition-all duration-200 ease-in-out hover:bg-paper-inset"
                  onClick={() => history.push('/payments/new')}
                >
                  <div>
                    <p className="font-semibold text-ink-secondary">Payment</p>
                    <p className="mt-0.5 text-[11px] font-normal text-ink-muted">
                      Process a new transaction
                    </p>
                  </div>
                  <svg
                    className="ml-2 h-4 w-4 text-ink-muted transition-transform duration-200 ease-in-out group-hover:translate-x-1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </DropdownMenuItem>
              ))}

            {checkPermission(P.PROCESS_ELEMENTS) && (
              <DropdownMenuItem
                className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-sm transition-all duration-200 ease-in-out hover:bg-paper-inset"
                onClick={() =>
                  // Non-entitled (Starter/downgraded) → the Elements PAGE
                  // (advertising or read-only view), never the creation UI.
                  history.push(canCreateElement ? '/elements/new' : '/elements')
                }
              >
                <div>
                  <p className="font-semibold text-ink-secondary">Web Element</p>
                  <p className="mt-0.5 text-[11px] font-normal text-ink-muted">
                    Create an embeddable element
                  </p>
                </div>
                <svg
                  className="ml-2 h-4 w-4 text-ink-muted transition-transform duration-200 ease-in-out group-hover:translate-x-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </DropdownMenuItem>
            )}

            {marketingEnabled && checkPermission(P.CREATE_BROADCASTS) && (
              <DropdownMenuItem
                className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-sm transition-all duration-200 ease-in-out hover:bg-paper-inset"
                onClick={() =>
                  // Non-entitled → the Broadcasts PAGE, never the creation UI.
                  history.push(canCreateBroadcast ? '/broadcasts/new' : '/broadcasts')
                }
              >
                <div>
                  <p className="font-semibold text-ink-secondary">Broadcast</p>
                  <p className="mt-0.5 text-[11px] font-normal text-ink-muted">
                    Create a campaign or trigger
                  </p>
                </div>
                <svg
                  className="ml-2 h-4 w-4 text-ink-muted transition-transform duration-200 ease-in-out group-hover:translate-x-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </DropdownMenuItem>
            )}

            {marketingEnabled && checkPermission(P.CREATE_SEGMENTS) && (
              <DropdownMenuItem
                className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-sm transition-all duration-200 ease-in-out hover:bg-paper-inset"
                onClick={() =>
                  // Non-entitled → the Segments PAGE, never the creation UI.
                  canCreateSegment
                    ? history.push('/segments/new', { freshEmbed: Date.now() })
                    : history.push('/segments')
                }
              >
                <div>
                  <p className="font-semibold text-ink-secondary">Segment</p>
                  <p className="mt-0.5 text-[11px] font-normal text-ink-muted">
                    Create a new segment
                  </p>
                </div>
                <svg
                  className="ml-2 h-4 w-4 text-ink-muted transition-transform duration-200 ease-in-out group-hover:translate-x-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </DropdownMenuItem>
            )}

            {/* "Email template" create item removed (PE-1671). */}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
